import React, { useRef } from 'react';
import { ThreeEvent, useThree, invalidate } from '@react-three/fiber';
import * as THREE from 'three';
import { editorStore } from '../../store/editorStore';
import { MAX_INTERACTION_PLANE_SIZE } from '../../constants/canvas';
import { getBackdropHitTarget } from '../FloorPlan/BackdropImage';
import { markPanningDragFinished } from '../../utils/panningGesture';

const PAN_DRAG_THRESHOLD_PX = 2;

const PanningPlane: React.FC = () => {
    const { camera, viewport, gl } = useThree();
    const isPanning = useRef(false);
    const isLeftButtonPanning = useRef(false);
    const hasDragged = useRef(false);
    const startPoint = useRef<THREE.Vector2>(new THREE.Vector2());
    const cameraStart = useRef<THREE.Vector3>(new THREE.Vector3());

    const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
        if (!editorStore.panning && event.button === 0 && getBackdropHitTarget(event.clientX, event.clientY, gl, camera)) {
            return;
        }

        // Only process the pointerdown if it's the middle mouse button
        if (event.button === 1 && !editorStore.view3D && !editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.areaConstructionMode && !editorStore.areaFillConstructionMode && !editorStore.areaBleedFillMode && !editorStore.rulerConstructionMode) {
            editorStore.setPanning(true);
            isPanning.current = true;
            isLeftButtonPanning.current = false;
            hasDragged.current = false;
            startPoint.current.set(event.clientX, event.clientY);
            cameraStart.current.copy(camera.position);
            document.body.style.cursor = 'grabbing';
            invalidate();  // Invalidate the render to reflect changes
        } else if (event.button === 0 && editorStore.panning) {
            event.stopPropagation();
            isPanning.current = true;
            isLeftButtonPanning.current = true;
            hasDragged.current = false;
            startPoint.current.set(event.clientX, event.clientY);
            cameraStart.current.copy(camera.position);
            document.body.style.cursor = 'grabbing';
            invalidate();  // Invalidate the render to reflect changes
        }
    };

    const handlePointerMove = (event: PointerEvent) => {
        if (isPanning.current && editorStore.panning) {
            const pointerDeltaX = event.clientX - startPoint.current.x;
            const pointerDeltaY = event.clientY - startPoint.current.y;

            if (!hasDragged.current) {
                const movedFarEnough =
                    Math.abs(pointerDeltaX) > PAN_DRAG_THRESHOLD_PX ||
                    Math.abs(pointerDeltaY) > PAN_DRAG_THRESHOLD_PX;

                if (!movedFarEnough) {
                    return;
                }

                hasDragged.current = true;
            }

            if (
                !(
                    camera instanceof THREE.PerspectiveCamera ||
                    camera instanceof THREE.OrthographicCamera
                )
            ) {
                return;
            }

            let newX: number;
            let newY: number;

            // Orthographic pan math is admin-only (True 2D). Everyone else keeps
            // the legacy perspective formula from main.
            if (camera instanceof THREE.OrthographicCamera) {
                const viewportWidth =
                    Math.abs(camera.right - camera.left) / Math.max(camera.zoom, 1e-6);
                const viewportHeight =
                    Math.abs(camera.top - camera.bottom) / Math.max(camera.zoom, 1e-6);
                const canvasHeight = Math.max(gl.domElement.clientHeight, 1);
                const worldPerPixel = viewportHeight / canvasHeight;
                const deltaX = pointerDeltaX * worldPerPixel;
                const deltaY = pointerDeltaY * worldPerPixel;

                newX = cameraStart.current.x - deltaX;
                newY = cameraStart.current.y + deltaY;

                const halfViewportWidth = viewportWidth / 2;
                const halfViewportHeight = viewportHeight / 2;
                const planeHalfSize = MAX_INTERACTION_PLANE_SIZE / 2;

                if (viewportWidth < MAX_INTERACTION_PLANE_SIZE) {
                    const minX = -planeHalfSize + halfViewportWidth;
                    const maxX = planeHalfSize - halfViewportWidth;
                    newX = Math.max(minX, Math.min(maxX, newX));
                }

                if (viewportHeight < MAX_INTERACTION_PLANE_SIZE) {
                    const minY = -planeHalfSize + halfViewportHeight;
                    const maxY = planeHalfSize - halfViewportHeight;
                    newY = Math.max(minY, Math.min(maxY, newY));
                }
            } else {
                const deltaX = pointerDeltaX / camera.zoom / 44;
                const deltaY = pointerDeltaY / camera.zoom / 44;

                newX = cameraStart.current.x - deltaX;
                newY = cameraStart.current.y + deltaY;

                const { width: viewportWidth, height: viewportHeight } =
                    viewport.getCurrentViewport(camera);

                const halfViewportWidth = viewportWidth / 2;
                const halfViewportHeight = viewportHeight / 2;
                const planeHalfSize = MAX_INTERACTION_PLANE_SIZE / 2;

                let minX = -planeHalfSize + halfViewportWidth;
                let maxX = planeHalfSize - halfViewportWidth;

                if (viewportWidth >= MAX_INTERACTION_PLANE_SIZE) {
                    minX = maxX = 0;
                    newX = 0;
                } else {
                    newX = Math.max(minX, Math.min(maxX, newX));
                }

                let minY = -planeHalfSize + halfViewportHeight;
                let maxY = planeHalfSize - halfViewportHeight;

                if (viewportHeight >= MAX_INTERACTION_PLANE_SIZE) {
                    minY = maxY = 0;
                    newY = 0;
                } else {
                    newY = Math.max(minY, Math.min(maxY, newY));
                }
            }

            camera.position.set(newX, newY, camera.position.z);
            camera.up.set(0, 1, 0);
            camera.lookAt(newX, newY, 0);
            camera.updateMatrixWorld();
            editorStore.setCamera(camera);
            editorStore.remember2DCameraView(camera);
            invalidate(); // Invalidate the render to reflect changes
        }
    };

    const handlePointerUp = (event: PointerEvent) => {
        if (event.button === 1) {
            editorStore.setPanning(false);
        }

        if (isPanning.current) {
            if (hasDragged.current) {
                markPanningDragFinished();
            }

            if (isLeftButtonPanning.current && !hasDragged.current) {
                editorStore.clearSelections();
            }
            isPanning.current = false;
            isLeftButtonPanning.current = false;
            hasDragged.current = false;
            if (
                camera instanceof THREE.PerspectiveCamera ||
                camera instanceof THREE.OrthographicCamera
            ) {
                camera.up.set(0, 1, 0);
                camera.lookAt(camera.position.x, camera.position.y, 0);
                camera.updateMatrixWorld();
                editorStore.setCamera(camera);
                editorStore.remember2DCameraView(camera);
            }
            document.body.style.cursor = 'auto';
        }
    };

    // Validate plane size to prevent invalid geometry
    const planeSize = MAX_INTERACTION_PLANE_SIZE * 1.5;
    const isValidSize = Number.isFinite(planeSize) && planeSize > 0 && planeSize <= 10000;
    
    if (!isValidSize) {
        console.warn(`[PanningPlane] Invalid plane size: ${planeSize}, using default 450`);
    }

    return (
        <mesh
            position={[0, 0, -0.01]}
            onPointerDown={handlePointerDown}
            onPointerMove={(e) => handlePointerMove(e.nativeEvent)}
            onPointerUp={(e) => handlePointerUp(e.nativeEvent)}
            userData={{ id: 'panning-plane' }}
        >
            <planeGeometry args={[isValidSize ? planeSize : 450, isValidSize ? planeSize : 450]} />
            <meshBasicMaterial transparent opacity={0} />
        </mesh>
    );
};

export default PanningPlane;
